import type { IdvSendTiming } from '@common/enums';
import type { CreateProspectManually } from '@common/types/prospects';
import { isValidEmail } from '@common/utils/isValidEmail';
import { formatPhoneNumber, toE164PhoneNumber } from '@common/utils/phone';
import { getPhoneValidationError } from '@/utils/phone';

// The clients in the New Prospect modal, and their checks. Kept out of the components so they
// can be tested, and reused when a client is picked from the firm's contacts

export interface ClientDetailsEntry {
    /** Identifies the row while the form is open. Not sent */
    rowId: string;
    firstName: string;
    middleName: string;
    lastName: string;
    contactEmail: string;
    /** Digits only, without the dialing code. Sent as people read it, such as (416) 555-0100 */
    phoneNumber: string;
    /** Lowercase ISO country code of the phone, such as 'ca'. Only used to read phoneNumber */
    phoneCountryCode: string;
    /**
     * When to send this client's ID verification automatically, or null not to. Undefined until
     * someone chooses, so it follows the firm's default (withDefaultIdvOptions)
     */
    idvSendTiming: IdvSendTiming | null | undefined;
    /** Whether that ID verification asks for a second ID. Undefined follows the firm's default */
    idvSecondaryIdRequired: boolean | undefined;
    /** Whether it asks for banking verification. Undefined follows the firm's default */
    idvBankingVerificationRequired: boolean | undefined;
    isPrimary: boolean;
}

export interface ClientDetailsErrors {
    firstName?: string;
    lastName?: string;
    contactEmail?: string;
    phoneNumber?: string;
}

export function createClientDetailsEntry(
    overrides: Partial<ClientDetailsEntry> = {},
): ClientDetailsEntry {
    return {
        rowId: crypto.randomUUID(),
        firstName: '',
        middleName: '',
        lastName: '',
        contactEmail: '',
        phoneNumber: '',
        phoneCountryCode: 'ca',
        idvSendTiming: undefined,
        idvSecondaryIdRequired: undefined,
        idvBankingVerificationRequired: undefined,
        isPrimary: false,
        ...overrides,
    };
}

/** What each client's automatic ID verification starts at: the firm's Intake Options */
export interface IdvDefaults {
    idvSendTiming: IdvSendTiming | null;
    idvSecondaryIdRequired: boolean;
    idvBankingVerificationRequired: boolean;
}

/**
 * The client with the firm's defaults for automatic ID verification, where nobody chose for them.
 * Applied when reading the clients, since the firm can load after the first client is created
 */
export function withDefaultIdvOptions(
    client: ClientDetailsEntry,
    defaults: IdvDefaults,
): ClientDetailsEntry {
    return {
        ...client,
        idvSendTiming:
            client.idvSendTiming === undefined ? defaults.idvSendTiming : client.idvSendTiming,
        idvSecondaryIdRequired: client.idvSecondaryIdRequired ?? defaults.idvSecondaryIdRequired,
        idvBankingVerificationRequired:
            client.idvBankingVerificationRequired ?? defaults.idvBankingVerificationRequired,
    };
}

/** One client's own errors. Duplicate emails across clients are checked when the form is sent */
export function getClientDetailsErrors(client: ClientDetailsEntry): ClientDetailsErrors {
    const email = client.contactEmail.trim();
    const phoneError =
        client.phoneNumber || client.idvSendTiming
            ? getPhoneValidationError(client.phoneNumber, client.phoneCountryCode, {
                  emptyMessage: 'A cell number is needed to send ID verification',
              })
            : '';
    return {
        firstName: client.firstName.trim() ? undefined : 'First name is required.',
        lastName: client.lastName.trim() ? undefined : 'Last name is required.',
        contactEmail: !email
            ? 'Email is required.'
            : isValidEmail(email)
              ? undefined
              : 'Invalid email.',
        phoneNumber: phoneError || undefined,
    };
}

export function hasClientDetailsErrors(errors: ClientDetailsErrors): boolean {
    return Object.values(errors).some(Boolean);
}

/**
 * A client as the API takes it, with the phone as people read it. getClientDetailsErrors has
 * already checked the phone, so one that isn't a valid number is only possible when it's empty
 */
export function toCreateContact(
    client: ClientDetailsEntry,
): CreateProspectManually['contacts'][number] {
    const middleName = client.middleName.trim();
    const e164PhoneNumber = toE164PhoneNumber(client.phoneNumber, client.phoneCountryCode);
    const phoneNumber = e164PhoneNumber && formatPhoneNumber(e164PhoneNumber);
    return {
        firstName: client.firstName.trim(),
        middleName: middleName || undefined,
        lastName: client.lastName.trim(),
        contactEmail: client.contactEmail.trim(),
        ...(phoneNumber && { phoneNumber }),
        idvSendTiming: client.idvSendTiming ?? null,
        // Only asked for when ID verification is sent automatically
        idvSecondaryIdRequired: Boolean(client.idvSendTiming && client.idvSecondaryIdRequired),
        idvBankingVerificationRequired: Boolean(
            client.idvSendTiming && client.idvBankingVerificationRequired,
        ),
        isPrimary: client.isPrimary,
    };
}
